import { ReactNode, useCallback, useMemo, useState, useEffect } from "react";
import "../../css/CreateEvent.css";
import {
  FormInputType,
  NotificationType,
  SavedFormQuestion,
  Event,
  CustomTicketV2,
  ThemeType,
} from "@markit/common.types";
import { DarkTheme, LightTheme, useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import CreateEventSectionHeader from "../CreateEvent/CreateEventSectionHeader";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { questionInputName } from "../../utils/eventUtils/formUtils";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../redux/store";
import {
  LoginState,
  accountActions,
  removeSingleProfileQuestion,
  reorderProfileQuestions,
} from "../../redux/slices/accountSlice";
import { filterUndefinedValues, uniqueVals } from "@markit/common.utils";
import FormSavedQuestions, {
  FormSavedQuestionsType,
} from "./FormSavedQuestions";
import FormBuilder from "./FormBuilder";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { showNotificationBanner } from "../../utils/notificationUtils";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import DragDropList from "../Lists/DragDropList";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import ThreeDotsButton from "../Buttons/ThreeDotsButton";
import CreateConditionalQuestionLogicModal from "../CreateEvent/CreateModals/CreateConditionalQuestionLogicModal";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import isEqual from "lodash.isequal";
import DragDropListIcon from "../Lists/DragDropListIcon";
import RectangleButton from "../Buttons/RectangleButton";
import CollectedByDefaultBanner from "./CollectedByDefaultBanner";

interface FormBuilderWrapperProps {
  isProfileQuestions: boolean;
  hostId: string;
  event?: Event;
  updateEventSettings?: (update: Partial<Event>) => void;
  themeType?: ThemeType;
  altHeader?: ReactNode;
  disableAnimation?: boolean;
}

const FormBuilderWrapper = (props: FormBuilderWrapperProps) => {
  const {
    isProfileQuestions,
    hostId,
    event,
    updateEventSettings,
    themeType,
    altHeader,
    disableAnimation,
  } = props;
  const { theme } = useTheme(themeType);
  const { currentEventFormQuestions, savedQuestions, accountData, loggedIn } =
    useSelector((state: AppState) => state.account);
  const dispatch = useDispatch();
  const [isMinimized, setIsMinimized] = useState(false);
  const [savedQuestionsVisible, setSavedQuestionsVisible] = useState(false);
  const [formQuestionItem, setFormQuestionItem] = useState<SavedFormQuestion>();
  const [deleteFormQuestionItem, setDeleteFormQuestionItem] =
    useState<SavedFormQuestion>();
  const [viewConditionalLogicModal, setViewConditionalLogicModal] =
    useState(false);
  const [selectedFormQuestion, setSelectedFormQuestion] =
    useState<SavedFormQuestion>();
  const [alertText, setAlertText] = useState("");

  const profileFormQuestions = useMemo(() => {
    const formQuestions = accountData.formQuestions.map((questionId) =>
      savedQuestions.find((question) => question.id === questionId)
    );
    const formQuestionsDefined: SavedFormQuestion[] =
      filterUndefinedValues(formQuestions);
    return formQuestionsDefined;
  }, [accountData.formQuestions, savedQuestions]);

  const currentFormQuestions = useMemo(
    () =>
      isProfileQuestions ? profileFormQuestions : currentEventFormQuestions,
    [currentEventFormQuestions, isProfileQuestions, profileFormQuestions]
  );

  // While Google Calendar invites are on, the first email question is where guests'
  // addresses come from, so it can't be deleted
  const lockedQuestionId = useMemo(
    () =>
      !isProfileQuestions && event?.googleCalendarInvites
        ? currentFormQuestions.find(
            (question) => question.formInput.inputType === FormInputType.EMAIL
          )?.id
        : undefined,
    [currentFormQuestions, event?.googleCalendarInvites, isProfileQuestions]
  );

  // Update custom tickets with new form questions when currentEventFormQuestions change
  useEffect(() => {
    if (
      !isProfileQuestions &&
      event &&
      updateEventSettings &&
      currentEventFormQuestions.length > 0
    ) {
      const currentQuestionIds = currentEventFormQuestions.map((q) => q.id);

      // Find questions that are completely new (not on any ticket type yet)
      const allExistingQuestionIds = event.customTickets.flatMap(
        (ticketType) => ticketType.formQuestions
      );
      const brandNewQuestionIds = currentQuestionIds.filter(
        (questionId) => !allExistingQuestionIds.includes(questionId)
      );

      // Update each custom ticket to include/exclude form questions
      const updatedCustomTickets = event.customTickets.map((ticketType) => {
        // Remove form questions that no longer exist
        const filteredFormQuestions = ticketType.formQuestions.filter(
          (questionId) => currentQuestionIds.includes(questionId)
        );

        // Only add brand new questions (not on any ticket type yet)
        const newFormQuestions = brandNewQuestionIds.filter(
          (questionId) => !ticketType.formQuestions.includes(questionId)
        );

        return {
          ...ticketType,
          formQuestions: uniqueVals([
            ...filteredFormQuestions,
            ...newFormQuestions,
          ]),
        };
      });

      // Only update if there are actual changes
      const hasChanges = updatedCustomTickets.some((ticketType, index) => {
        const originalTicket = event.customTickets[index];
        return !isEqual(ticketType.formQuestions, originalTicket.formQuestions);
      });

      if (hasChanges) {
        updateEventSettings({
          customTickets: updatedCustomTickets,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEventFormQuestions]);

  const multipleCustomTicketTypes = useMemo(
    () => (event ? event.customTickets.length > 1 : false),
    [event]
  );

  const showCohostAlert = useCallback(() => {
    if (!isProfileQuestions && hostId !== accountData.uid) {
      setAlertText("Cohost cannot modify form questions");
      return true;
    }
    return false;
  }, [accountData.uid, hostId, isProfileQuestions]);

  const updateCustomTicketFormQuestions = useCallback(
    (newFormQuestions: SavedFormQuestion[]) => {
      if (event && updateEventSettings) {
        updateEventSettings({
          customTickets: event.customTickets.map((ticket) => {
            const foundQuestionIds = newFormQuestions
              .filter((question) => ticket.formQuestions.includes(question.id))
              .map((question) => question.id);
            return {
              ...ticket,
              formQuestions: foundQuestionIds,
            };
          }),
        });
      }
    },
    [event, updateEventSettings]
  );

  const handleOnDrag = useCallback(
    (newFormQuestions: SavedFormQuestion[]) => {
      if (isProfileQuestions) {
        dispatch(
          reorderProfileQuestions(
            hostId,
            newFormQuestions.map((question) => question.id)
          )
        );
      } else {
        updateCustomTicketFormQuestions(newFormQuestions);
        dispatch(
          accountActions.reorderCurrentEventFormQuestions(newFormQuestions)
        );
      }
    },
    [dispatch, hostId, isProfileQuestions, updateCustomTicketFormQuestions]
  );

  const newQuestionOnPress = useCallback(
    (currQuestion?: SavedFormQuestion) => {
      if (!showCohostAlert()) {
        if (currQuestion) {
          setFormQuestionItem(currQuestion);
        } else {
          setSavedQuestionsVisible(true);
        }
      }
    },
    [showCohostAlert]
  );

  const deleteQuestionOnPress = useCallback(
    (currQuestion: SavedFormQuestion) => {
      if (
        currQuestion &&
        currQuestion.id !== lockedQuestionId &&
        !showCohostAlert()
      ) {
        setDeleteFormQuestionItem(currQuestion);
      }
    },
    [lockedQuestionId, showCohostAlert]
  );

  const deleteFormQuestion = useCallback(() => {
    if (!deleteFormQuestionItem) {
      setAlertText("There was an issue deleting this question.");
      return;
    }

    if (isProfileQuestions) {
      dispatch(removeSingleProfileQuestion(hostId, deleteFormQuestionItem.id));
      showNotificationBanner(
        dispatch,
        "Question Removed",
        NotificationType.NEGATIVE
      );
    } else {
      updateCustomTicketFormQuestions(
        currentFormQuestions.filter(
          (question) => question.id !== deleteFormQuestionItem.id
        )
      );
      dispatch(
        accountActions.deleteCurrentEventFormQuestion(deleteFormQuestionItem.id)
      );
    }
    setDeleteFormQuestionItem(undefined);
  }, [
    deleteFormQuestionItem,
    isProfileQuestions,
    dispatch,
    hostId,
    updateCustomTicketFormQuestions,
    currentFormQuestions,
  ]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Edit Conditional Logic",
        icon: "material-symbols:call-split",
        key: "edit_conditional_logic",
        onPress: () => setViewConditionalLogicModal(true),
      },
    ],
    [setViewConditionalLogicModal]
  );

  const renderFormBuilder = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 10 }}>
        {currentFormQuestions.length > 0 ? (
          <DragDropList
            items={currentFormQuestions}
            handleChange={handleOnDrag}
          >
            {(question: SavedFormQuestion, _, dragHandleProps: any) => (
              <div
                style={{
                  paddingBlock: 14,
                  borderRadius: 12,
                  backgroundColor: theme
                    ? theme.NewPrimaryBG.backgroundColor
                    : Colors.WHITE1,
                  border: `0.5px solid ${
                    theme?.EditableTextFieldBorder.borderColor || Colors.GRAY2
                  }`,
                }}
              >
                <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
                  <div className="AlignedRow" style={{ gap: 5 }}>
                    <DragDropListIcon
                      dragHandleProps={dragHandleProps}
                      size={22}
                    />
                    <span
                      style={{
                        color: theme?.LabelText.color || Colors.GRAY2,
                        fontSize: 12,
                      }}
                    >
                      {questionInputName(question.formInput.inputType)}
                    </span>
                    {question.formInput.isRequired ? (
                      <span
                        style={{
                          color: Colors.RED1,
                          fontSize: 12,
                        }}
                      >
                        *Required
                      </span>
                    ) : null}
                  </div>
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    {question.id === lockedQuestionId ? (
                      <span
                        className="Centering"
                        title="Needed for Google Calendar invites. Turn off Google Calendar Invites in event settings to remove it."
                      >
                        <Icon
                          icon={"ion:lock-closed"}
                          height={18}
                          style={{ color: Colors.GRAY2 }}
                        />
                      </span>
                    ) : (
                      <>
                        {multipleCustomTicketTypes ? (
                          <DropdownMenu
                            dropdownItems={dropdownItems}
                            trigger={["click"]}
                            placement="bottomLeft"
                            altWidth={212}
                            dark={theme === DarkTheme}
                          >
                            <ThreeDotsButton
                              onPress={() => {
                                setSelectedFormQuestion(question);
                              }}
                              themeProp={theme}
                            />
                          </DropdownMenu>
                        ) : null}
                        <Icon
                          className="TrashButton"
                          icon={"ion:trash"}
                          height={20}
                          onClick={() => deleteQuestionOnPress(question)}
                        />
                        <Icon
                          className={
                            "EditButton " +
                            (theme === LightTheme || !theme
                              ? "EditButtonLight"
                              : "EditButtonDark")
                          }
                          icon={"ion:create"}
                          height={20}
                          onClick={() => newQuestionOnPress(question)}
                        />
                      </>
                    )}
                  </div>
                </div>
                <HorizontalDivider
                  altMargin={10}
                  altWidth={1}
                  altColor={theme?.DividerColor.borderColor}
                />
                <div
                  className="ColumnNormal"
                  style={{ paddingInline: 14, gap: 7 }}
                >
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    {question.isEssential ? (
                      <div>
                        <Icon
                          icon="fa:map-pin"
                          height={14}
                          style={{
                            color: theme?.PrimaryText.color,
                          }}
                        />
                      </div>
                    ) : null}
                    <span
                      className="AboutSubtitle"
                      style={{ fontSize: 14, ...theme?.PrimaryText }}
                    >
                      {question.formInput.label}
                    </span>
                  </div>
                  {question.formInput.inputType === FormInputType.RADIO ||
                  question.formInput.inputType === FormInputType.CHECK_BOX ? (
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      {question.formInput.options.map(
                        (option: string, index: number) => (
                          <div
                            key={index}
                            className="AlignedRow"
                            style={{ gap: 6 }}
                          >
                            {question.formInput.inputType ===
                            FormInputType.RADIO ? (
                              <span
                                style={{
                                  color: Colors.GRAY2,
                                  fontSize: 12,
                                }}
                              >
                                #{index + 1}
                              </span>
                            ) : (
                              <div
                                style={{
                                  padding: 4,
                                  border: "1px solid #B9B9B9",
                                }}
                              />
                            )}
                            <span
                              style={{
                                fontSize: 12,
                                color: Colors.GRAY2,
                              }}
                            >
                              {option}
                            </span>
                          </div>
                        )
                      )}
                      {question.formInput.hasOtherOption ? (
                        <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                          Other...
                        </span>
                      ) : null}
                    </div>
                  ) : question.formInput.inputType ===
                    FormInputType.FILE_UPLOAD ? (
                    <div className="AlignedRow" style={{ gap: 4 }}>
                      <Icon
                        icon="ion:document"
                        height={14}
                        style={{ color: Colors.GRAY2 }}
                      />
                      <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                        Upload File
                      </span>
                    </div>
                  ) : (
                    <div className="AlignedRow" style={{ gap: 4 }}>
                      <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                        {question.formInput.placeholder || "Answer here..."}
                      </span>
                    </div>
                  )}
                  {event &&
                  event.customTickets.some(
                    (customTicket) =>
                      !customTicket.formQuestions.includes(question.id)
                  ) ? (
                    <>
                      <HorizontalDivider
                        altMargin={10}
                        altWidth={1}
                        altColor={theme?.DividerColor.borderColor}
                      />
                      <div
                        className="bodySubtextMedium"
                        style={{ marginBottom: 7 }}
                      >
                        Only Show Question For
                      </div>
                      <div className="ColumnNormal" style={{ gap: 4 }}>
                        {event.customTickets
                          .filter((ticketTypes: CustomTicketV2) =>
                            ticketTypes.formQuestions.includes(question.id)
                          )
                          .map((ticketType) => (
                            <div
                              key={ticketType.id}
                              className="smallBodySubtext"
                            >
                              {ticketType.label}
                            </div>
                          ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </DragDropList>
        ) : null}
        {isProfileQuestions && currentFormQuestions.length === 0 ? (
          <EmptyStateButton
            title={"Add Questions to Collect Info"}
            description={
              "Collect custom info on subscribe. Your questions will show up here."
            }
            icon={
              <Icon
                icon="ion:document"
                height={50}
                style={{ color: Colors.BLUE5 }}
              />
            }
            iconBox={84}
            containerStyles={{ paddingBlock: 80 }}
            btnText={"Add Question"}
            onPress={() => newQuestionOnPress()}
          />
        ) : (
          <div className="onboarding-sell-products-questions-target">
            <RectangleButton
              buttonLabel={<span className="bodyMedium">Add Question</span>}
              onPress={() => newQuestionOnPress()}
              altColor={
                theme ? theme.NewPrimaryBG.backgroundColor : Colors.WHITE1
              }
              altTextColor={theme ? theme.PrimaryText.color : Colors.BLACK}
              iconRight={
                <Icon
                  icon="ion:add-circle"
                  height={18}
                  color={theme?.PrimaryText.color || Colors.BLACK}
                />
              }
              containerStyles={{
                paddingInline: 30,
                paddingBlock: 14,
                border: `0.5px solid ${
                  theme?.EditableTextFieldBorder.borderColor || Colors.GRAY2
                }`,
              }}
            />
          </div>
        )}
      </div>
    ),
    [
      currentFormQuestions,
      handleOnDrag,
      isProfileQuestions,
      theme,
      multipleCustomTicketTypes,
      dropdownItems,
      event,
      deleteQuestionOnPress,
      lockedQuestionId,
      newQuestionOnPress,
    ]
  );

  return (
    <div className="ColumnNormal" style={{ gap: 14 }}>
      {event &&
      updateEventSettings &&
      viewConditionalLogicModal &&
      selectedFormQuestion ? (
        <CreateConditionalQuestionLogicModal
          event={event}
          updateEventSettings={updateEventSettings}
          selectedFormQuestion={selectedFormQuestion}
          setIsVisible={setViewConditionalLogicModal}
          theme={theme || LightTheme}
        />
      ) : null}
      {altHeader ?? (
        <CreateEventSectionHeader
          title="Collect Info"
          description={
            isProfileQuestions
              ? "Collect additional subscriber data on initial subscribe"
              : undefined
          }
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          theme={theme}
        />
      )}
      {!isMinimized ? (
        <CollectedByDefaultBanner theme={theme || LightTheme} />
      ) : null}
      {!isMinimized ? (
        disableAnimation ? (
          <div className="HideScrollbar">{renderFormBuilder}</div>
        ) : (
          <AnimatePresence>
            <LazyMotion features={domAnimation}>
              <m.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeIn" }}
                className="HideScrollbar"
              >
                {renderFormBuilder}
              </m.div>
            </LazyMotion>
          </AnimatePresence>
        )
      ) : null}
      {(loggedIn !== LoginState.LOGGED_IN && savedQuestionsVisible) ||
      formQuestionItem ? (
        <FormBuilder
          type={
            isProfileQuestions
              ? FormSavedQuestionsType.PROFILE
              : FormSavedQuestionsType.EVENT
          }
          setIsFormsModalOpen={setSavedQuestionsVisible}
          selectedQuestions={currentEventFormQuestions}
          setSelectedQuestions={(formQuestions: SavedFormQuestion[]) => {
            dispatch(
              accountActions.reorderCurrentEventFormQuestions(formQuestions)
            );
          }}
          formQuestionItem={formQuestionItem}
          setFormQuestionItem={setFormQuestionItem}
          theme={theme}
        />
      ) : savedQuestionsVisible ? (
        <FormSavedQuestions
          theme={theme}
          type={
            isProfileQuestions
              ? FormSavedQuestionsType.PROFILE
              : FormSavedQuestionsType.EVENT
          }
          eventId={event?.id}
          isVisible={savedQuestionsVisible}
          setIsVisible={setSavedQuestionsVisible}
        />
      ) : null}
      <ConfirmDeleteModal
        heading="Are you sure you want to delete this question?"
        subtext="You cannot undo this."
        deleteButtonText="Delete"
        hideModal={!deleteFormQuestionItem}
        setIsVisible={(visible: boolean) => {
          if (!visible) {
            setDeleteFormQuestionItem(undefined);
          }
        }}
        deleteOnPress={deleteFormQuestion}
        theme={theme}
      />
      <AlertContainer
        headerComp={alertText}
        theme={theme}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
    </div>
  );
};

export default FormBuilderWrapper;
